import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FaListUl } from "react-icons/fa6";
import { LuPin, LuStar } from 'react-icons/lu';
import { AllTrucksReportByWeeks } from './weeklyGrossByTrucks';
import{Link} from 'react-router-dom';
import { WeeklyGrossByTeams } from './weeklyGrossByTeams';

import { retrieveCompanyData } from '../../supplementary_components/companySettings';
import { can, retrieveCustomization, retrieveCustomizations, storeCustomizations } from '../../supplementary_components/userDataDelegate';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { addNotification } from '../../supplementary_components/store';
import { pinPath, sanitizePins, unpinPath } from '../../general_components/main_menu/menuModel';
import { groupReports, reportById, reportMenuPath } from './reportCatalog';
import { useClassicMenu } from '../../general_components/main_menu/menuLayout';
import { ExpensesPage } from './expenses';
import { MonthlyExpensesPage } from './monthlyExpenses';
import { WeeklyGrossByDispatcher } from './weeklyGrossByDispatcher';
import { LoadsForSelectedWeek } from './loadsForSelectedPeriod';
import { InvoicesReportForSelectedWeek } from './invoicesReportByPeriod';
import { OwnerLeaseReportForSelectedWeek } from './ownerLeaseReport';
import { FleetPerformanceReport } from './fleetPerformance';
import { MonthlyCompanyReport } from './monthlyCompanyReports';
import { EquipmentPaymentsReport } from './equipmentPaymentsReport';
import { EfsMoneyCodeManagement } from './efsMoneyCodeManagement';
import { PayableInvoicesManagement } from './payableInvoicesManagement';
import { FuelCardTransactionsPage } from './fuelCardTransactions';

const ReportsContent = styled.div`
	&>.subTitle{
		font-size:1.2rem;
		font-weight: 700;
		color:var(--dark-gray);
	}
	&>.availableReports{
		padding: 1rem 0;
		display:flex;
		
	}
`;
const ClickableItem = styled(Link)`
	position: relative;
	border:solid thin var(--lighter-gray);
	margin:0.5rem;
	height:4rem;
	width:20rem;
	display:flex;
	align-items:center;
	padding:1rem;
	border-radius:0.4rem;
	font-weight:700;
	color:var(--gray);
	cursor:pointer;
	text-decoration: none;
	:hover{
		box-shadow: var(--lighter-gray) 0px 6px 12px -2px, var(--extra-light-gray) 0px 3px 7px -3px;
	}
`;

const StarButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.3rem;
	height: 1.3rem;
	margin-right: 0.5rem;
	padding: 0;
	border: none;
	background: transparent;
	color: ${props => props.$favorite ? 'var(--main-color)' : 'inherit'};
	cursor: pointer;
	flex-shrink: 0;
	svg { width: 1.05rem; height: 1.05rem; }
`;

const PinButton = styled.button`
	position: absolute;
	top: 0.3rem;
	right: 0.3rem;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.3rem;
	height: 1.3rem;
	padding: 0;
	border: none;
	border-radius: 6px;
	background: var(--bg);
	color: ${props => props.$pinned ? 'var(--dark-gray)' : 'var(--light-gray)'};
	cursor: pointer;
	svg {
		width: 0.95rem;
		height: 0.95rem;
		transform: ${props => props.$pinned ? 'none' : 'rotate(-40deg)'};
	}
`;

const readList = (key) => {
	const value = retrieveCustomization(key);
	return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
};

const saveCustomization = (key, next, previous, apply) => {
	const write = (value) => {
		storeCustomizations({ ...retrieveCustomizations(), [key]: value });
	};
	apply(next);
	write(next);
	sendRequest(
		'PATCH',
		API_ROOT + 'employees/customizations',
		null,
		(updated) => {
			storeCustomizations(updated);
			apply(Array.isArray(updated?.[key]) ? updated[key] : next);
		},
		() => {
			apply(previous);
			write(previous);
			addNotification({ message: 'Could not save', type: 'alert' });
		},
		{ [key]: next },
	);
};

const ReportBox = ({ report, favorite, pinned, showPin, onToggleFavorite, onTogglePin }) => {
	const [hover, setHover] = useState(false);
	const [pinReady, setPinReady] = useState(false);
	const timer = useRef(null);
	useEffect(() => () => {
		if (timer.current) clearTimeout(timer.current);
	}, []);
	const enter = () => {
		setHover(true);
		if (!showPin) return;
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(() => setPinReady(true), 700);
	};
	const leave = () => {
		setHover(false);
		setPinReady(false);
		if (timer.current) clearTimeout(timer.current);
	};
	const stop = (event) => {
		event.preventDefault();
		event.stopPropagation();
	};
	return (
		<ClickableItem to={`reports/${report.id}`} onMouseEnter={enter} onMouseLeave={leave}>
			<StarButton
				type="button"
				$favorite={favorite}
				aria-label={favorite ? `Remove ${report.title} from favorites` : `Add ${report.title} to favorites`}
				onClick={(event) => { stop(event); onToggleFavorite(report.id); }}
			>
				{favorite || hover ? <LuStar fill={favorite ? 'currentColor' : 'none'} /> : <FaListUl />}
			</StarButton>
			<span>{report.title}</span>
			{showPin && pinReady ? (
				<PinButton
					type="button"
					$pinned={pinned}
					aria-label={pinned ? `Unpin ${report.title}` : `Pin ${report.title}`}
					onClick={(event) => { stop(event); onTogglePin(report.id); }}
				>
					<LuPin />
				</PinButton>
			) : null}
		</ClickableItem>
	);
};

const ReportIndex = () => {
	const classicMenu = useClassicMenu();
	const [favorites, setFavorites] = useState(() => readList('reportFavorites'));
	const [pins, setPins] = useState(() => sanitizePins(retrieveCustomization('menuPins')));
	const { favorites: favoriteReports, groups } = groupReports(favorites, can);
	const pinned = new Set(pins);

	const toggleFavorite = (id) => {
		if (!reportById(id)) return;
		const next = favorites.includes(id) ? favorites.filter((item) => item !== id) : [...favorites, id];
		saveCustomization('reportFavorites', next, favorites, setFavorites);
	};

	const togglePin = (id) => {
		const target = reportMenuPath(id);
		if (pins.includes(target)) {
			saveCustomization('menuPins', unpinPath(pins, target), pins, setPins);
			return;
		}
		const result = pinPath(pins, target, () => true);
		if (result.error) {
			addNotification({ message: result.error, type: 'alert' });
			return;
		}
		if (result.changed) saveCustomization('menuPins', result.pins, pins, setPins);
	};

	const renderGroup = (name, items) => (
		<React.Fragment key={name}>
			<div className="subTitle">{name}</div>
			<div className="availableReports">
				{items.map((report) => (
					<ReportBox
						key={report.id}
						report={report}
						favorite={favorites.includes(report.id)}
						pinned={pinned.has(reportMenuPath(report.id))}
						showPin={!classicMenu}
						onToggleFavorite={toggleFavorite}
						onTogglePin={togglePin}
					/>
				))}
			</div>
		</React.Fragment>
	);

	return (
		<ReportsContent>
			{favoriteReports.length > 0 ? renderGroup('Favorites', favoriteReports) : null}
			{groups.map((group) => renderGroup(group.name, group.items))}
		</ReportsContent>
	);
};

export const Reports = ({itemIdentifier}) => {
	const companyData = retrieveCompanyData();
	let elementIdentifier = null;
	if(itemIdentifier.includes("/")){
		elementIdentifier = itemIdentifier.split("/")[1];
		itemIdentifier = itemIdentifier.split("/")[0];
	}
	switch (itemIdentifier) {
		case "expenses":
			return <ExpensesPage companyData={companyData} />
		case "monthlyExpenses":
			return <MonthlyExpensesPage companyData={companyData} />
		case "invoices":
			return <InvoicesReportForSelectedWeek companyData={companyData} />
		case "weeklyGrossByTrucks":
			return <AllTrucksReportByWeeks companyData={companyData} />
		case "weeklyGrossByTeams":
			return <WeeklyGrossByTeams companyData={companyData} />
		case "weeklyGrossByDispatch":
			return <WeeklyGrossByDispatcher companyData={companyData} />
		case "loadsByPeriod":
			return <LoadsForSelectedWeek companyData={companyData} />
		case "ownerLeaseReports":
		case "rateAdjustmentsReport":
			return <OwnerLeaseReportForSelectedWeek companyData={companyData} />
		case "fleetPerformance":
			return <FleetPerformanceReport companyData={companyData} />
		case "monthlyCompanyReports":
			return <MonthlyCompanyReport companyData={companyData} />
		case "truckPayments":
			return <EquipmentPaymentsReport equipment="trucks" />
		case "trailerPayments":
			return <EquipmentPaymentsReport equipment="trailers" />
		case "moneyCodeManagement":
			return <EfsMoneyCodeManagement companyData={companyData} />
		case "payableInvoicesManagement":
			return <PayableInvoicesManagement companyData={companyData} elementIdentifier={elementIdentifier} />
		case "fuelCardTransactions":
			return <FuelCardTransactionsPage companyData={companyData} />
		default:
			break;
	}
	return <>
		<div className='pageHeader'>
			<div>Reports</div>
		</div>
		<ReportIndex />
	</>
}